import { logger } from '@/utils/logger';

const RECOVERY_STORAGE_KEY = 'ilotto:chunk-recovery';
const RECOVERY_COOLDOWN_MS = 30_000;
const MAX_RECOVERY_ATTEMPTS = 2;
const RECOVERY_EPISODE_TTL_MS = 10 * 60_000;

const CHUNK_ERROR_PATTERNS = [
  /Loading chunk [\w-]+ failed/i,
  /Loading CSS chunk/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
];

const AMBIGUOUS_CHUNK_ERROR_PATTERNS = [
  /Cannot read propert(?:y|ies) of undefined \(reading '?call'?\)/i,
  /undefined is not an object \(evaluating '.*\.call'\)/i,
];

const WEBPACK_RUNTIME_PATTERNS = [
  /__webpack_require__/,
  /webpack-[\w-]*\.js/i,
  /options\.factory/,
];

function readErrorText(error: unknown): string {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (typeof error !== 'object') return '';

  const candidate = error as { name?: unknown; message?: unknown; stack?: unknown };
  return [candidate.name, candidate.message, candidate.stack]
    .filter((value): value is string => typeof value === 'string')
    .join('\n');
}

export type ChunkErrorConfidence = 'high' | 'low';

export function getChunkErrorConfidence(error: unknown): ChunkErrorConfidence | null {
  if (
    error &&
    typeof error === 'object' &&
    (error as { name?: unknown }).name === 'ChunkLoadError'
  ) {
    return 'high';
  }

  const text = readErrorText(error);
  if (!text) return null;

  if (CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(text))) return 'high';

  if (!AMBIGUOUS_CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(text))) return null;

  return WEBPACK_RUNTIME_PATTERNS.some((pattern) => pattern.test(text)) ? 'high' : 'low';
}

export function isChunkLoadError(error: unknown): boolean {
  return getChunkErrorConfidence(error) !== null;
}

type RecoveryState = { at: number; count: number };

function readRecoveryState(now: number): RecoveryState | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(RECOVERY_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as { at?: unknown; count?: unknown };
    if (typeof parsed?.at !== 'number' || typeof parsed?.count !== 'number') return null;
    if (now - parsed.at > RECOVERY_EPISODE_TTL_MS) return null;

    return { at: parsed.at, count: parsed.count };
  } catch {
    return null;
  }
}

function persistRecoveryState(state: RecoveryState): boolean {
  try {
    const storage = globalThis.sessionStorage;
    if (!storage) return false;

    const payload = JSON.stringify(state);
    storage.setItem(RECOVERY_STORAGE_KEY, payload);

    return storage.getItem(RECOVERY_STORAGE_KEY) === payload;
  } catch {
    return false;
  }
}

export function attemptChunkRecovery(context: string): boolean {
  if (typeof window === 'undefined') return false;

  const now = Date.now();
  const state = readRecoveryState(now);

  if (state && now - state.at < RECOVERY_COOLDOWN_MS) {
    logger.warn('[chunk-recovery] skipped, recent attempt', {
      channel: 'chunk-error',
      context,
      url: globalThis.location.href,
      msSinceLastAttempt: now - state.at,
      attempts: state.count,
    });
    return false;
  }

  if (state && state.count >= MAX_RECOVERY_ATTEMPTS) {
    logger.error('[chunk-recovery] giving up, attempt cap reached', {
      channel: 'chunk-error',
      context,
      url: globalThis.location.href,
      attempts: state.count,
    });
    return false;
  }

  const nextState: RecoveryState = { at: now, count: (state?.count ?? 0) + 1 };

  if (!persistRecoveryState(nextState)) {
    logger.error('[chunk-recovery] skipped, recovery state is not persistable', {
      channel: 'chunk-error',
      context,
      url: globalThis.location.href,
    });
    return false;
  }

  logger.error('[chunk-recovery] reloading', {
    channel: 'chunk-error',
    context,
    url: globalThis.location.href,
    attempt: nextState.count,
  });

  globalThis.location.reload();
  return true;
}

export function isNextStaticResourceError(event: Event): boolean {
  const target = event.target;

  if (target instanceof HTMLScriptElement) {
    return target.src.includes('/_next/static/');
  }

  if (target instanceof HTMLLinkElement) {
    return target.href.includes('/_next/static/');
  }

  return false;
}
